/**
 * The decisions the bulk review screen makes about its own list, kept pure so
 * they can be tested without a gateway, a router or a DOM.
 *
 * The screen itself owns loading, focus and the writes; everything here is a
 * function of the items already in hand. Nothing in this file reaches for a
 * field's `text` or `finalText` — the delivered value arrives on the queue
 * item as `value`, through `resultFieldValue`, and that is the only value
 * this screen ever shows or submits.
 */

import type { ReviewQueueItem } from '@/components/review'
import type { EvaluationDocument } from '@/types/domain'

// -- The type filter -------------------------------------------------------

export type TypeFilter = 'all' | 'text' | 'checkbox' | 'signature'

/** The filter row, in the artboard's order. Labels are fixed vocabulary. */
export const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'text', label: 'Text' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'signature', label: 'Signatur' },
]

/**
 * How many of each type are still waiting.
 *
 * Counted over the *open* items, not over everything loaded: the tab says how
 * much work is left, and a count that keeps standing at 24 while the person
 * clears the list is a count nobody believes twice. It also means a tab can
 * reach 0 while its rows are still on screen for the undo window — which is
 * why `filterByType` never uses these numbers to decide what to render.
 */
export function typeCounts(items: ReviewQueueItem[]): Record<TypeFilter, number> {
  const counts: Record<TypeFilter, number> = { all: 0, text: 0, checkbox: 0, signature: 0 }
  for (const item of items) {
    if (item.reviewed) continue
    counts.all += 1
    counts[item.type] += 1
  }
  return counts
}

export function filterByType(items: ReviewQueueItem[], filter: TypeFilter): ReviewQueueItem[] {
  if (filter === 'all') return items
  return items.filter((item) => item.type === filter)
}

// -- The sort --------------------------------------------------------------

export type SortKey = 'confidence' | 'document' | 'label'

export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'confidence', label: 'Niedrigste Sicherheit zuerst' },
  { value: 'document', label: 'Dokument' },
  { value: 'label', label: 'Feld' },
]

/**
 * A field with nothing in it sorts as 0, whatever confidence the recognizer
 * reported.
 *
 * "Niedrigste Sicherheit zuerst" means "worst first", and nothing recognised
 * is the worst case there is; a confident empty reading (it happens — the
 * model is sure it saw nothing) would otherwise sit below a 40 % reading that
 * at least has letters in it.
 */
function sortConfidence(item: ReviewQueueItem): number {
  return item.flag?.kind === 'empty' ? 0 : item.confidence
}

function documentTitle(item: ReviewQueueItem): string {
  return item.documentTitle ?? item.documentId
}

/** Sorts a copy. Ties fall back to document then label, so the order is total
 *  and does not shuffle when a page of documents is appended. */
export function sortItems(items: ReviewQueueItem[], key: SortKey): ReviewQueueItem[] {
  const byDocument = (a: ReviewQueueItem, b: ReviewQueueItem) =>
    documentTitle(a).localeCompare(documentTitle(b), 'de')
  const byLabel = (a: ReviewQueueItem, b: ReviewQueueItem) => a.label.localeCompare(b.label, 'de')

  const comparators: Record<SortKey, (a: ReviewQueueItem, b: ReviewQueueItem) => number> = {
    confidence: (a, b) => sortConfidence(a) - sortConfidence(b) || byDocument(a, b) || byLabel(a, b),
    document: (a, b) => byDocument(a, b) || byLabel(a, b),
    label: (a, b) => byLabel(a, b) || byDocument(a, b),
  }
  return [...items].sort(comparators[key])
}

// -- Progress --------------------------------------------------------------

export interface BulkProgress {
  done: number
  total: number
  /** 0..100, for the bar's width. 0 on an empty list rather than NaN. */
  percent: number
}

/** "7 von 31 erledigt", over the fields loaded so far. The denominator grows
 *  as more documents are paged in and never shrinks, because a reviewed field
 *  stays in the list it was counted in. */
export function progressOf(items: ReviewQueueItem[]): BulkProgress {
  const total = items.length
  const done = items.filter((item) => item.reviewed).length
  return { done, total, percent: total === 0 ? 0 : Math.round((done / total) * 100) }
}

// -- Paging ----------------------------------------------------------------

/**
 * Whether to pull the next page of documents.
 *
 * The rhythm of this screen is Enter, Enter, Enter; running out of rows mid-
 * rhythm and having to click "mehr laden" breaks it. So the next page is
 * fetched while there is still work on screen — not when the list is empty.
 */
export const PREFETCH_BELOW = 8

export function shouldLoadMore(
  openCount: number,
  { hasMore, loading }: { hasMore: boolean; loading: boolean },
): boolean {
  return hasMore && !loading && openCount < PREFETCH_BELOW
}

// -- The page behind a crop ------------------------------------------------

/**
 * Which stored image a field's rectangle may be drawn on.
 *
 * A result field's `x/y/x2/y2` are in the template's own frame — the frame the
 * worker warps a scan into before it reads it, and the frame the *analysed*
 * and *enhanced* page images are stored in. `imagePath` is the upload as it
 * arrived: photographed at an angle, its own resolution, never straightened.
 * Drawing the box on that one puts a rectangle somewhere arbitrary on the
 * page, which reads as a recognition failure rather than as the unit error it
 * is — so the original is not offered at all, and a document with neither
 * rendered page simply has no preview.
 */
export function previewImagePath(doc: EvaluationDocument | undefined): string | null {
  if (!doc) return null
  return doc.enhancedImagePath || doc.evalImagePath || null
}

// -- The undo window -------------------------------------------------------

/**
 * What one just-written row can still offer.
 *
 * A review cannot be unwritten: the gateway stores reviews as history and has
 * no delete. What it *can* do is record another one. So the honest offer
 * depends on what was written:
 *
 * - a **correction** can be undone for real, by correcting back to the value
 *   that was there before (the server history stays truthful — two
 *   corrections, which is what happened). Including a correction away from an
 *   empty value: the endpoint now stores a correction to `''`, so putting the
 *   blank back is an ordinary write like any other;
 * - an **acceptance** changed no value, so there is nothing to put back. The
 *   only truthful offer is to open the row again and keep working on it.
 *
 * The artboard draws "Rückgängig" on every save. Offering it for an
 * acceptance would be a button claiming to remove a review it cannot remove.
 */
export interface SavedWrite {
  key: string
  label: string
  /** The delivered value before this write. */
  previousValue: string
  corrected: boolean
}

export type UndoKind = 'repost' | 'reopen'

export function undoKind(write: SavedWrite): UndoKind {
  return write.corrected ? 'repost' : 'reopen'
}

export const UNDO_LABEL: Record<UndoKind, string> = {
  repost: 'Rückgängig',
  reopen: 'Erneut öffnen',
}

/** The toast's sentence. Fixed vocabulary; the field name is quoted the way
 *  the design quotes it. */
export function savedMessage(write: SavedWrite): string {
  return write.corrected ? `„${write.label}“ korrigiert` : `„${write.label}“ übernommen`
}

/** How long a save stays undoable. Long enough to notice the toast, short
 *  enough that it is gone before the next field is decided. */
export const UNDO_MS = 8_000
